import React, { useState, useEffect, useRef } from 'react';
import { Collapse, Table, Typography, Button } from 'antd';
import { DownOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * SimpleTableDisplay component for displaying a dictionary/object as a table
 * 
 * @param {Object} props
 * @param {Object} props.data - The data object containing the dictionary to display
 * @param {string} props.title - The title of the accordion
 * @param {string} props.uuid - The UUID of the data object
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 * @param {string} props.sessionId - The current session ID
 * @param {string} props.project_id - The project ID
 * @param {string} props.agent_key - The agent key
 */
const SimpleTableDisplay = ({
  data,
  title = "Table Display",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent,
  sessionId,
  project_id,
  agent_key
}) => {
  // State to track whether the component is expanded
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Use useEffect to update the state after render
  const expandIconRef = useRef(null);

  useEffect(() => {
    if (expandIconRef.current !== null) {
      setIsExpanded(expandIconRef.current);
      expandIconRef.current = null;
    }
  }, []);

  // Register this component for updates if the function is provided
  useEffect(() => {
    if (registerComponent && uuid) {
      // Create a refresh function to update this component
      const refreshComponent = () => {
        console.log(`Refreshing Component: ${uuid}`);
        // This will trigger a re-render
        setIsExpanded(prev => prev);
      };
      
      // Register this component with the provided function
      const unregister = registerComponent(refreshComponent);
      
      // Unregister when the component unmounts
      return unregister;
    }
  }, [registerComponent, uuid]);

  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };

  // Convert dictionary data to table format
  const getTableData = () => {
    if (!data || typeof data !== 'object') return [];
    
    const tableData = [];
    let rowKey = 0;

    // Handle the main title separately if it exists
    const displayData = { ...data };
    const tableTitle = displayData.Title || displayData.title || title;
    
    // Remove Title from the data to display
    delete displayData.Title;
    delete displayData.title;

    Object.entries(displayData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // For arrays, create multiple rows
        value.forEach((item, index) => {
          tableData.push({
            key: rowKey++,
            item: index === 0 ? key : '',
            value: item
          });
        });
      } else if (value !== null && value !== undefined) {
        // For regular values
        tableData.push({
          key: rowKey++,
          item: key,
          value: String(value)
        });
      }
    });

    return { tableData, tableTitle };
  };

  // Table columns definition
  const columns = [
    {
      title: 'Item',
      dataIndex: 'item',
      key: 'item',
      width: '40%',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: '60%'
    }
  ];

  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;

  const { tableData, tableTitle } = getTableData();

  return (
    <div style={{ 
      marginBottom: '4px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px'
    }}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => {
          expandIconRef.current = isActive;
          return isActive ? <DownOutlined /> : <RightOutlined />;
        }}
        items={[
          {
            key: '1',
            label: (
              <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
                {tableTitle}
              </div>
            ),
            children: (
              <div>
                {tableData.length > 0 ? (
                  <Table
                    columns={columns}
                    dataSource={tableData}
                    pagination={false}
                    size="small"
                    bordered
                    style={{ marginBottom: '16px' }}
                  />
                ) : (
                  <Text>No data available to display.</Text>
                )}
                
                {/* Version navigation */}
                {showVersionNavigation && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '16px', 
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <Button 
                      size="small" 
                      icon={<LeftOutlined />}
                      disabled={currentVersion <= Math.min(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex > 0) {
                          handleVersionChange(versions[currentIndex - 1]);
                        }
                      }}
                    >
                      Previous
                    </Button>
                    
                    <div>
                      Version {currentVersion} of {versionInfo.total_versions}
                    </div>
                    
                    <Button 
                      size="small" 
                      icon={<RightOutlined />}
                      disabled={currentVersion >= Math.max(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex < versions.length - 1) {
                          handleVersionChange(versions[currentIndex + 1]);
                        }
                      }}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )
          }
        ]}
      />
    </div>
  );
};

export default SimpleTableDisplay;